/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { PuffLoader } from "react-spinners";
import { FaSearch } from "react-icons/fa";
import Card from "../../components/shared/Card";
import Button from "../../components/shared/Button";
import { searchUsers, impersonateUser } from "../../api/impersonation";
import { useAuth } from "../../context/AuthContext";

// La API distingue mayúsculas en el filtro de rol: sólo acepta "User".
const ROL_SUPLANTABLE = "User";

const ImpersonateUser = () => {
  const { startImpersonation } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [primeraCarga, setPrimeraCarga] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [impersonatingId, setImpersonatingId] = useState(null);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    fromRecord: 0,
    toRecord: 0,
    totalRecords: 0,
  });

  const debounceTimeoutRef = useRef(null);

  const loadUsers = async (page = 1, search = "") => {
    try {
      setLoading(true);
      const response = await searchUsers({
        search,
        role: ROL_SUPLANTABLE,
        page,
        limit: pageSize,
      });

      const data = response.data || [];
      setUsers(data);

      if (response.meta) {
        setPagination({
          currentPage: response.meta.current_page || 1,
          lastPage: response.meta.last_page || 1,
          fromRecord: response.meta.from_record || 0,
          toRecord: response.meta.to_record || 0,
          totalRecords: response.meta.total_records || 0,
        });
      } else {
        setPagination({
          currentPage: 1,
          lastPage: 1,
          fromRecord: data.length > 0 ? 1 : 0,
          toRecord: data.length,
          totalRecords: data.length,
        });
      }
    } catch (error) {
      console.error("Error loading users:", error);
      setUsers([]);
      setPagination({
        currentPage: 1,
        lastPage: 1,
        fromRecord: 0,
        toRecord: 0,
        totalRecords: 0,
      });
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error || "Error al cargar los usuarios",
      });
    } finally {
      setLoading(false);
      setPrimeraCarga(false);
    }
  };

  useEffect(() => {
    loadUsers(1, "");
  }, []);

  useEffect(() => {
    if (!primeraCarga) {
      loadUsers(1, searchTerm);
    }
  }, [pageSize]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      loadUsers(1, value);
    }, 300);
  };

  const handleSearch = () => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    loadUsers(1, searchTerm);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > pagination.lastPage || loading) return;
    loadUsers(page, searchTerm);
  };

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value);
    if (newSize !== pageSize) {
      setPageSize(newSize);
    }
  };

  const getNombreCompleto = (usuario) =>
    [usuario.first_name, usuario.last_name].filter(Boolean).join(" ") ||
    usuario.username ||
    "Sin nombre";

  const handleImpersonate = async (usuario) => {
    const nombre = getNombreCompleto(usuario);

    const confirmacion = await Swal.fire({
      icon: "question",
      title: "¿Suplantar a este usuario?",
      text: `Vas a ver la aplicación como ${nombre} (${usuario.email}). Podrás volver al panel de administración en cualquier momento.`,
      showCancelButton: true,
      confirmButtonText: "Suplantar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#009ee3",
    });

    if (!confirmacion.isConfirmed) return;

    try {
      setImpersonatingId(usuario.id);
      const respuesta = await impersonateUser(usuario.id);
      startImpersonation(respuesta.user);
      navigate("/dashboard");
    } catch (error) {
      setImpersonatingId(null);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error || "No se pudo iniciar la suplantación",
      });
    }
  };

  const renderPagination = () => {
    const pages = [];
    const { currentPage, lastPage } = pagination;

    if (lastPage >= 1) {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(lastPage, startPage + 4);

      if (endPage - startPage < 4) {
        startPage = Math.max(1, endPage - 4);
      }

      if (startPage > 1) {
        pages.push(
          <button
            key="1"
            className={`pagination__button ${
              currentPage === 1 ? "pagination__button--active" : ""
            }`}
            onClick={() => handlePageChange(1)}
            disabled={loading}
          >
            1
          </button>,
        );
        if (startPage > 2) {
          pages.push(
            <span key="start-ellipsis" className="pagination__ellipsis">
              ...
            </span>,
          );
        }
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <button
            key={i}
            className={`pagination__button ${
              currentPage === i ? "pagination__button--active" : ""
            }`}
            onClick={() => handlePageChange(i)}
            disabled={loading}
          >
            {i}
          </button>,
        );
      }

      if (endPage < lastPage) {
        if (endPage < lastPage - 1) {
          pages.push(
            <span key="end-ellipsis" className="pagination__ellipsis">
              ...
            </span>,
          );
        }
        pages.push(
          <button
            key={lastPage}
            className={`pagination__button ${
              currentPage === lastPage ? "pagination__button--active" : ""
            }`}
            onClick={() => handlePageChange(lastPage)}
            disabled={loading}
          >
            {lastPage}
          </button>,
        );
      }
    }

    return (
      <div className="pagination">
        <button
          className="pagination__button pagination__button--nav"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1 || loading}
        >
          &laquo;
        </button>
        {pages}
        <button
          className="pagination__button pagination__button--nav"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === lastPage || loading}
        >
          &raquo;
        </button>
      </div>
    );
  };

  const renderPageSizeSelector = () => {
    return (
      <div className="pagination__size-selector">
        <label htmlFor="pageSize" className="pagination__size-label">
          Mostrar:
        </label>
        <select
          id="pageSize"
          className="filter-select pagination__size-select"
          value={pageSize}
          onChange={handlePageSizeChange}
          disabled={loading}
        >
          <option value="5">5</option>
          <option value="10">10</option>
          <option value="20">20</option>
          <option value="50">50</option>
        </select>
      </div>
    );
  };

  if (loading && primeraCarga) {
    return (
      <div className="loader-container">
        <PuffLoader
          color="#009ee3"
          loading={true}
          size={60}
          margin={2}
          speedMultiplier={4}
        />
      </div>
    );
  }

  const renderFilas = () => {
    if (loading) {
      return (
        <tr>
          <td colSpan="5" className="no-data">
            <PuffLoader
              color="#009ee3"
              loading={true}
              size={40}
              margin={2}
              speedMultiplier={4}
            />
          </td>
        </tr>
      );
    }

    if (users.length === 0) {
      return (
        <tr>
          <td colSpan="5" className="no-data">
            No se encontraron usuarios
          </td>
        </tr>
      );
    }

    return users.map((usuario) => (
      <tr key={usuario.id}>
        <td>{getNombreCompleto(usuario)}</td>
        <td>{usuario.email || "Sin correo"}</td>
        <td>{usuario.username || "-"}</td>
        <td>
          <span
            className={`impersonate-user__status ${
              usuario.is_active
                ? "impersonate-user__status--active"
                : "impersonate-user__status--inactive"
            }`}
          >
            {usuario.is_active ? "Activo" : "Inactivo"}
          </span>
        </td>
        <td>
          <Button
            variant="outline"
            onClick={() => handleImpersonate(usuario)}
            disabled={usuario.is_active === false || impersonatingId !== null}
          >
            {impersonatingId === usuario.id ? "Suplantando..." : "Suplantar"}
          </Button>
        </td>
      </tr>
    ));
  };

  return (
    <Card className={"impersonate-user-card"}>
      <div className="impersonate-user">
        <div className="impersonate-user__header">
          <h2 className="impersonate-user__title">Suplantar usuario</h2>
          <p className="impersonate-user__description">
            Busca una cuenta de usuario para ver la aplicación tal como la ve
            esa persona. Podrás volver al panel de administración en cualquier
            momento.
          </p>
        </div>

        <div className="impersonate-user__toolbar">
          <div className="impersonate-user__search">
            <div className="input-group__search-container">
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchInputChange}
                onKeyDown={handleSearchKeyDown}
                className="input-group__input-search"
                placeholder="Buscar por nombre o correo"
              />
              <button
                className="input-group__search-button"
                onClick={handleSearch}
              >
                <FaSearch />
              </button>
            </div>
          </div>

          <div className="impersonate-user__filters">
            {renderPageSizeSelector()}
          </div>
        </div>

        <div className="impersonate-user__table">
          <table>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Correo</th>
                <th>Usuario</th>
                <th>Estado</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>{renderFilas()}</tbody>
          </table>
        </div>

        <div className="impersonate-user__pagination">
          <div className="pagination__container">
            <div>{renderPagination()}</div>
            <div className="pagination__info">
              Mostrando {pagination.fromRecord} a {pagination.toRecord} de{" "}
              {pagination.totalRecords} registros
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ImpersonateUser;
